import type {
    AuthService,
    BackstageCredentials,
} from "@backstage/backend-plugin-api";
import type { DiscoveryApi, IdentityApi } from "@backstage/core-plugin-api";
import { ResponseError } from "@backstage/errors";
import stableStringify from "fast-json-stable-stringify";

import type { ApiResponse, ApiResponseBody } from "../../types";
import {
    ApiOptions,
    BackendErrorWithDetails,
    Defineable,
    Nullable,
} from "../../types";
import { clientRegistry } from "./ClientRegistry";

/**
 * Construction options for {@link BackendClient}.
 *
 * @public
 */
export interface BackendClientParameters {
    /** Backstage plugin ID that this client targets (e.g. `"my-plugin"`). */
    targetPluginId: string;
    /** Module ID when this client represents a backend module, otherwise omit. */
    moduleId?: string;
    /** Discovery API used to resolve backend plugin base URLs. */
    discoveryApi: DiscoveryApi;
    /**
     * Identity source used to obtain auth tokens.
     * Pass an {@link IdentityApi} from the frontend or an {@link AuthService} from the backend.
     */
    identityApi: IdentityApi | AuthService;
    /**
     * Credentials to act on behalf of, when `identityApi` is an {@link AuthService}.
     *
     * Without this the client authenticates as the calling backend's own service
     * identity, so the target plugin sees a service principal rather than the
     * person who triggered the work. Supply the initiator's credentials when the
     * target authorizes a user.
     */
    onBehalfOf?: BackstageCredentials;
}

/**
 * Base client for making authenticated HTTP requests to Backstage backend plugins.
 *
 * Extend this class in `common` plugin packages to provide typed API methods.
 * The {@link BackendClient.api} method handles URL construction, auth token injection,
 * optional response caching, and structured error enrichment.
 *
 * Module sub-clients self-register by calling {@link createClientModule} at module
 * evaluation time. They are mounted directly onto the instance during construction
 * via {@link clientRegistry}.
 *
 * @example
 * ```typescript
 * class MyPluginClient extends BackendClient {
 *   constructor(options: BackendClientParameters) {
 *     super(options);
 *   }
 *
 *   async getWidget(id: string): Promise<ApiResponse<Widget>> {
 *     return this.api<Widget>(`/widgets/${id}`);
 *   }
 * }
 * ```
 *
 * @public
 */
export class BackendClient {
    /** The Backstage plugin ID this client targets. */
    public readonly targetPluginId: string;

    /** Module ID when this client represents a backend module, otherwise `undefined`. */
    public readonly moduleId: Defineable<string>;

    readonly #discoveryApi: DiscoveryApi;
    readonly #identityApi: IdentityApi | AuthService;
    readonly #onBehalfOf?: BackstageCredentials;
    readonly #apiCache = new Map<string, Promise<ApiResponse<unknown>>>();

    /**
     * Creates a new {@link BackendClient} instance and mounts any registered
     * module sub-clients directly onto it via {@link clientRegistry}.
     *
     * @param options - Client configuration.
     */
    constructor(options: BackendClientParameters) {
        this.targetPluginId = options.targetPluginId;
        this.moduleId = options.moduleId;
        this.#discoveryApi = options.discoveryApi;
        this.#identityApi = options.identityApi;
        this.#onBehalfOf = options.onBehalfOf;

        clientRegistry.mount(
            this as unknown as Record<string, unknown>,
            options.targetPluginId,
            {
                discoveryApi: options.discoveryApi,
                identityApi: options.identityApi,
                onBehalfOf: options.onBehalfOf,
            }
        );
    }

    /**
     * Makes an authenticated API request to the backend with optional caching support.
     *
     * Returns an {@link ApiResponse} that mirrors the `fetch` `Response` shape:
     * - `headers` -- HTTP response headers
     * - `status` -- HTTP status code
     * - `ok` -- whether the request succeeded (status 2xx)
     * - `body` -- parsed JSON body as {@link ApiResponseBody}
     *
     * When `options.cacheTTL` is set the response promise is keyed by `(fullUrl, init)`.
     * Calls within the TTL window return the same promise without issuing a new request.
     *
     * @param path - API endpoint path relative to the plugin base URL (e.g. `"/widgets/123"`).
     * @param init - Optional `fetch` request init (method, body, headers, etc.).
     * @param options - Optional call-level options including caching configuration.
     * @returns A promise resolving to an {@link ApiResponse}.
     *
     * @example
     * ```typescript
     * const result = await this.api<Widget>('/widgets/123');
     *
     * if (result.body.ok) {
     *   return result.body.data;
     * } else {
     *   throw new Error(result.body.error);
     * }
     * ```
     */
    protected async api<T>(
        path: string,
        init?: RequestInit,
        options?: ApiOptions
    ): Promise<ApiResponse<T>> {
        const baseUrl = await this.#discoveryApi.getBaseUrl(
            this.targetPluginId
        );
        const fullUrl = this.#buildUrl(baseUrl, path);

        if (options?.cacheTTL !== undefined) {
            const cacheKey = this.#getCacheKey(fullUrl, init);
            const cached = this.#apiCache.get(cacheKey);
            if (cached) {
                return cached as Promise<ApiResponse<T>>;
            }

            const resultPromise = this.#performRequest<T>(fullUrl, init);
            this.#apiCache.set(cacheKey, resultPromise);
            setTimeout(() => this.#apiCache.delete(cacheKey), options.cacheTTL);

            return resultPromise;
        }

        return this.#performRequest<T>(fullUrl, init);
    }

    /**
     * Generates a stable cache key for a given URL and request init.
     *
     * @param url - The fully-resolved request URL.
     * @param init - Optional `fetch` request init options.
     * @returns A deterministic string key.
     */
    #getCacheKey(url: string, init?: RequestInit): string {
        const normalized = init
            ? {
                  ...init,
                  headers: Array.from(new Headers(init.headers).entries()),
              }
            : {};
        return `${url} ${stableStringify(normalized)}`;
    }

    /**
     * Combines a base URL and a path, ensuring exactly one `/` between them.
     *
     * @param baseUrl - The base URL from the discovery API.
     * @param path - The path to append.
     * @returns The full URL string.
     */
    #buildUrl(baseUrl: string, path: string): string {
        const base = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
        const normalizedPath = path.startsWith("/") ? path : `/${path}`;
        return `${base}${normalizedPath}`;
    }

    /**
     * Performs the actual HTTP request, injecting auth headers and handling errors.
     *
     * Throws a {@link ResponseError} enriched with `details` when the response is not ok.
     * Returns a synthetic body for `204 No Content` responses (no body to parse).
     *
     * @param fullUrl - The fully-resolved URL to request.
     * @param init - Optional `fetch` request init options.
     * @returns A promise resolving to an {@link ApiResponse}.
     */
    async #performRequest<T>(
        fullUrl: string,
        init?: RequestInit
    ): Promise<ApiResponse<T>> {
        const token = await this.#getToken();
        const headers = new Headers(init?.headers);

        if (!headers.has("content-type")) {
            headers.set("content-type", "application/json");
        }
        if (token && !headers.has("authorization")) {
            headers.set("authorization", `Bearer ${token}`);
        }

        const response = await fetch(
            new Request(fullUrl, { ...init, headers })
        );

        if (!response.ok) {
            throw await this.#handleErrorResponse(
                response,
                fullUrl,
                init?.method
            );
        }

        // 204 No Content: no body to parse
        if (response.status === 204) {
            return {
                headers: response.headers,
                status: response.status,
                ok: true,
                body: { ok: true, data: undefined as T },
            };
        }

        const body = (await response.json()) as ApiResponseBody<T>;
        return {
            headers: response.headers,
            status: response.status,
            ok: response.ok,
            body,
        };
    }

    /**
     * Parses an error response and returns a {@link ResponseError} enriched with structured details.
     *
     * The resulting `error.details` shape:
     * ```
     * {
     *   errorCode?: string,
     *   requestContext: {
     *     url: string,
     *     method: string,
     *     status: number,
     *     // ...any remaining fields from the API response details
     *   }
     * }
     * ```
     *
     * @param response - The non-ok HTTP response.
     * @param url - The URL that was requested.
     * @param method - The HTTP method used.
     * @returns A promise resolving to an enriched {@link ResponseError}.
     */
    async #handleErrorResponse(
        response: Response,
        url: string,
        method?: string
    ): Promise<ResponseError> {
        let errorMessage: Defineable<string>;
        let errorCode: unknown;
        let remainingDetails: Record<string, unknown> = {};

        try {
            const body = await response.clone().json();
            if (body && typeof body === "object") {
                errorMessage =
                    body.error?.message ?? body.error ?? body.message;
                if (body.details && typeof body.details === "object") {
                    const { errorCode: code, ...rest } = body.details as Record<
                        string,
                        unknown
                    >;
                    errorCode = code;
                    remainingDetails = rest;
                }
            }
        } catch {
            // Non-JSON error body -- ignore
        }

        const error = await ResponseError.fromResponse(response);

        if (errorMessage) {
            error.message = errorMessage;
        }

        const errorWithDetails = error as BackendErrorWithDetails;
        errorWithDetails.details = {
            ...(errorCode !== undefined
                ? { errorCode: String(errorCode) }
                : {}),
            requestContext: {
                url,
                method: method ?? "GET",
                status: response.status,
                ...remainingDetails,
            },
        };

        return error;
    }

    /**
     * Retrieves an auth token from the configured identity source.
     *
     * Supports both the frontend {@link IdentityApi} (detected via `getCredentials`)
     * and the backend {@link AuthService} (which uses `getPluginRequestToken`).
     *
     * @returns A promise resolving to the bearer token string, or `null` if unavailable.
     */
    async #getToken(): Promise<Nullable<string>> {
        let result: { token?: Defineable<string> };

        if ("getCredentials" in this.#identityApi) {
            result = await this.#identityApi.getCredentials();
        } else {
            result = await this.#identityApi.getPluginRequestToken({
                onBehalfOf:
                    this.#onBehalfOf ??
                    (await this.#identityApi.getOwnServiceCredentials()),
                targetPluginId: this.targetPluginId,
            });
        }

        return result.token ?? null;
    }
}
